import React, { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { customFieldService } from '../../services/custom-field.service'
import { documentService } from '../../services/document.service'
import { toastService } from '../../services/toast.service'
import { CustomField } from '../../types'
import { DocumentLinkInput } from '../common/DocumentLinkInput'
import { InputSelect } from '../common/InputSelect'

interface CustomFieldsBulkEditDialogProps {
  show: boolean
  onHide: () => void
  fieldsToEdit: CustomField[]
  fieldsToRemove?: CustomField[]
  documentIds: number[]
  onSuccess: () => void
}

enum CustomFieldDataType {
  String = 'string',
  Url = 'url',
  Date = 'date',
  Boolean = 'boolean',
  Integer = 'integer',
  Float = 'float',
  Monetary = 'monetary',
  DocumentLink = 'documentlink',
  Select = 'select',
  LongText = 'longtext',
}

export const CustomFieldsBulkEditDialog: React.FC<CustomFieldsBulkEditDialogProps> = ({
  show,
  onHide,
  fieldsToEdit,
  fieldsToRemove = [],
  documentIds,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [allCustomFields, setAllCustomFields] = useState<CustomField[]>([])
  const [fieldsToAddIds, setFieldsToAddIds] = useState<number[]>([])
  const [fieldsToRemoveIds, setFieldsToRemoveIds] = useState<number[]>([])
  const [formValues, setFormValues] = useState<{ [key: number]: any }>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const response = await customFieldService.listAll()
        const fields = Array.isArray(response) ? response : response.results || []
        // Separators carry no value, so they cannot participate in bulk edits.
        setAllCustomFields(fields.filter((f: CustomField) => f.data_type !== 'separator'))
      } catch (error) {}
    }
    if (show) {
      loadCustomFields()
      const initialIds = fieldsToEdit.map((f) => f.id)
      setFieldsToAddIds(initialIds)
      setFieldsToRemoveIds(fieldsToRemove.map((f) => f.id))
    }
  }, [show, fieldsToEdit, fieldsToRemove])

  useEffect(() => {
    const initialValues: { [key: number]: any } = {}
    fieldsToAddIds.forEach((fieldId) => {
      const field = allCustomFields.find((f) => f.id === fieldId)
      if (field) {
        if (field.data_type === CustomFieldDataType.DocumentLink) {
          initialValues[fieldId] = []
        } else if (field.data_type === CustomFieldDataType.Boolean) {
          initialValues[fieldId] = null
        } else {
          initialValues[fieldId] = null
        }
      }
    })
    setFormValues(initialValues)
  }, [fieldsToAddIds, allCustomFields])

  const handleFieldSelectionChange = (selectedIds: number[]) => {
    const removedIds = fieldsToAddIds.filter((id) => !selectedIds.includes(id))

    setFieldsToAddIds(selectedIds)

    if (removedIds.length > 0) {
      const newFormValues = { ...formValues }
      removedIds.forEach((id) => {
        delete newFormValues[id]
      })
      setFormValues(newFormValues)
    }
  }

  const handleValueChange = (fieldId: number, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }))
  }

  const handleRemoveField = (fieldId: number) => {
    setFieldsToAddIds((prev) => prev.filter((id) => id !== fieldId))
    const newFormValues = { ...formValues }
    delete newFormValues[fieldId]
    setFormValues(newFormValues)
  }

  const handleSave = async () => {
    if (fieldsToAddIds.length === 0 && fieldsToRemoveIds.length === 0) {
      toastService.showInfo(t('select_custom_fields'))
      return
    }

    setSaving(true)
    try {
      const addCustomFields: { [key: number]: any } = {}
      fieldsToAddIds.forEach((fieldId) => {
        if (formValues[fieldId] !== undefined && formValues[fieldId] !== null && formValues[fieldId] !== '') {
          addCustomFields[fieldId] = formValues[fieldId]
        } else {
          addCustomFields[fieldId] = null
        }
      })

      await documentService.bulkEditOperation(documentIds, 'modify_custom_fields', {
        add_custom_fields: addCustomFields,
        remove_custom_fields: fieldsToRemoveIds,
      })

      toastService.showInfo(t('custom_fields_updated'))
      onSuccess()
      onHide()
    } catch (error: any) {
      toastService.showError(t('error_updating_custom_fields'), error)
    } finally {
      setSaving(false)
    }
  }

  const renderFieldInput = (field: CustomField) => {
    const value = formValues[field.id]

    switch (field.data_type) {
      case CustomFieldDataType.DocumentLink:
        return (
          <DocumentLinkInput
            value={Array.isArray(value) ? value : []}
            onChange={(newValue) => handleValueChange(field.id, newValue)}
            disabled={false}
          />
        )

      case CustomFieldDataType.Select:
        return (
          <Form.Select value={value || ''} onChange={(e) => handleValueChange(field.id, e.target.value || null)}>
            <option value="">{t('select_value')}</option>
            {field.extra_data?.select_options?.map((option: any) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        )

      case CustomFieldDataType.Boolean:
        return (
          <Form.Select
            value={value === null ? '' : value === true || value === 'true' ? 'true' : 'false'}
            onChange={(e) => {
              const val = e.target.value === '' ? null : e.target.value === 'true'
              handleValueChange(field.id, val)
            }}
          >
            <option value="">{t('select_value')}</option>
            <option value="true">{t('true')}</option>
            <option value="false">{t('false')}</option>
          </Form.Select>
        )

      case CustomFieldDataType.Date:
        return <Form.Control type="date" value={value || ''} onChange={(e) => handleValueChange(field.id, e.target.value || null)} />

      case CustomFieldDataType.Integer:
        return (
          <Form.Control
            type="number"
            step="1"
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value || null)}
            placeholder={t('enter_value')}
          />
        )

      case CustomFieldDataType.Float:
      case CustomFieldDataType.Monetary:
        return (
          <div className="d-flex align-items-center gap-2">
            <Form.Control
              type="number"
              step="0.01"
              value={value || ''}
              onChange={(e) => handleValueChange(field.id, e.target.value || null)}
              placeholder={t('enter_value')}
            />
            {field.data_type === CustomFieldDataType.Monetary && <span className="text-muted">{field.extra_data?.default_currency || 'RSD'}</span>}
          </div>
        )

      case CustomFieldDataType.LongText:
        return (
          <Form.Control
            as="textarea"
            rows={3}
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value || null)}
            placeholder={t('enter_value')}
          />
        )

      case CustomFieldDataType.String:
      case CustomFieldDataType.Url:
      default:
        return (
          <Form.Control
            type={field.data_type === CustomFieldDataType.Url ? 'url' : 'text'}
            value={value || ''}
            onChange={(e) => handleValueChange(field.id, e.target.value || null)}
            placeholder={t('enter_value')}
          />
        )
    }
  }

  const fieldsToAdd = allCustomFields.filter((field) => fieldsToAddIds.includes(field.id))

  return (
    <Modal show={show} onHide={onHide} scrollable size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{t('set_custom_fields_for_documents', { count: documentIds.length })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InputSelect
          multiple={true}
          items={allCustomFields}
          value={fieldsToAddIds}
          onChange={handleFieldSelectionChange}
          placeholder={t('select_custom_fields')}
          disabled={saving}
        />
        <div className="d-flex flex-column gap-2 mt-3">
          {fieldsToAdd.map((field) => (
            <div key={field.id} className="d-flex gap-2 align-items-start">
              <div className="flex-grow-1">
                <Form.Group>
                  <Form.Label>{field.name}</Form.Label>
                  {renderFieldInput(field)}
                </Form.Group>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleRemoveField(field.id)}
                disabled={saving}
                className="mt-4"
                title={t('remove')}
              >
                <i className="bi bi-x"></i>
              </Button>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {fieldsToRemoveIds.length > 0 && (
          <p className="mb-0 small text-muted me-auto">
            <em>
              {fieldsToRemoveIds.length === 1
                ? t('this_operation_will_also_remove_custom_field', { count: fieldsToRemoveIds.length })
                : t('this_operation_will_also_remove_custom_fields', { count: fieldsToRemoveIds.length })}
            </em>
          </p>
        )}
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || (fieldsToAddIds.length === 0 && fieldsToRemoveIds.length === 0)}>
          {saving ? t('saving') : t('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
